import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Button, message, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BackButton } from '@/components/BackButton';
import { topTitleContext } from '@/contexts/TopTitle';
import { useDebounce } from '@/hooks/useDebounce';
import { hasOnlyNumber } from '@/components/ModalAddPlanogramFromProduct/_components/FormPlanogram/utils';
import { StoreBar } from '@/components/StoreBar';
import {
    QuickAdjustPlanogram as Planogram,
    SortOption,
    StatusFilterValue,
    ViewMode,
} from './types';
import { SearchAndFilters } from './_components/SearchAndFilters';
import { ProductCard } from './_components/ProductCard';
import { ProductTable } from './_components/ProductTable';
import { EditProductModal } from './_components/EditProductModal';
import { AddProductModal } from './_components/AddProductModal';
import { usePlanogramsByStore } from './usePlanogramsByStore';
import { formatProductCount, MIN_SEARCH_LENGTH } from './utils';
import * as S from './styles';
import './gridBreakpoints.css';

const SORT_TO_ORDER: Record<SortOption, { orderBy: string; orderDir: string }> =
    {
        category: { orderBy: 'category', orderDir: 'asc' },
        due_date: { orderBy: 'due_date', orderDir: 'asc' },
        stock: { orderBy: 'stock_gap', orderDir: 'asc' },
    };

const PlanogramQuickAdjust = () => {
    const { titleValue } = useContext(topTitleContext);
    const navigate = useNavigate();

    useEffect(() => {
        titleValue('Ajuste rápido de Planograma');
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();
    const storeIdParam = searchParams.get('storeId');
    const parsedStoreId = Number(storeIdParam);
    const activeStoreId =
        storeIdParam === null || Number.isNaN(parsedStoreId)
            ? undefined
            : parsedStoreId;
    const [search, setSearch] = useState('');
    const [categoryId, setCategoryId] = useState<number>();
    const [status, setStatus] = useState<StatusFilterValue>('unlocked');
    const [sort, setSort] = useState<SortOption>('category');
    const [viewMode, setViewMode] = useState<ViewMode>('cards');
    const [editingPlanogram, setEditingPlanogram] = useState<Planogram>();
    const [addModalOpen, setAddModalOpen] = useState(false);

    const debouncedSearch = useDebounce(search, 400);
    const searchLower = debouncedSearch.trim().toLowerCase();
    const isBarcodeSearch = hasOnlyNumber(searchLower);
    const { orderBy, orderDir } = SORT_TO_ORDER[sort];

    const {
        planograms,
        total,
        isLoading,
        isError,
        isSuccess,
        errorStatus,
        refetch,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = usePlanogramsByStore(activeStoreId, {
        name:
            !isBarcodeSearch && searchLower.length >= MIN_SEARCH_LENGTH
                ? searchLower
                : undefined,
        barcode: isBarcodeSearch ? searchLower : undefined,
        categoryId,
        status: status !== 'all' ? status : undefined,
        orderBy,
        orderDir,
    });

    const sentinelRef = useRef<HTMLDivElement>(null);

    const loadNextPage = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Cards only: the table view loads more from its own scroll
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadNextPage();
            },
            { rootMargin: '0px 0px 150px 0px' },
        );

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [loadNextPage, viewMode]);

    const resultLabel = formatProductCount(total);
    const isStoreNotFound = errorStatus === 401 || errorStatus === 404;

    const loadedStoreIdRef = useRef<number | undefined>(undefined);
    const shouldRedirect =
        isStoreNotFound && loadedStoreIdRef.current !== activeStoreId;

    useEffect(() => {
        if (isSuccess) loadedStoreIdRef.current = activeStoreId;
    }, [isSuccess, activeStoreId]);

    useEffect(() => {
        const { style } = document.documentElement;
        const previous = style.scrollbarGutter;
        style.scrollbarGutter = 'stable';
        return () => {
            style.scrollbarGutter = previous;
        };
    }, []);

    useEffect(() => {
        if (!shouldRedirect) return;
        message.warning({
            content: 'Loja não encontrada',
            key: 'store-not-found',
        });
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('storeId');
        setSearchParams(nextSearchParams, { replace: true });
    }, [shouldRedirect]);

    const handleOpenProduct = (productId: number) => {
        navigate(`/products/update/${productId}`);
    };

    const handleStoreChange = (storeId: number) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('storeId', String(storeId));
        setSearchParams(nextSearchParams, { replace: true });
    };

    return (
        <S.Container>
            <S.PrevButtonArea>
                <BackButton
                    label="Voltar para Ações Rápidas"
                    to="/quick-actions"
                />
            </S.PrevButtonArea>
            <S.Header>
                <S.Title>Ajuste rápido de Planograma</S.Title>
                <S.Description>
                    Visualize e ajuste as informações de planograma dos produtos
                    da loja selecionada.
                </S.Description>
            </S.Header>

            <StoreBar
                storeId={activeStoreId}
                onStoreChange={handleStoreChange}
                totalProducts={total}
                isLoading={isLoading}
            />

            {!activeStoreId && (
                <S.EmptyState>
                    <S.EmptyStateTitle>
                        Selecione uma loja para começar
                    </S.EmptyStateTitle>
                    <S.EmptyStateDescription>
                        Escolha a loja acima para ver os produtos do planograma.
                    </S.EmptyStateDescription>
                </S.EmptyState>
            )}

            {activeStoreId && (
                <>
                    <SearchAndFilters
                        search={search}
                        onSearchChange={setSearch}
                        categoryId={categoryId}
                        onCategoryChange={setCategoryId}
                        status={status}
                        onStatusChange={setStatus}
                        sort={sort}
                        onSortChange={setSort}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        resultLabel={resultLabel}
                        onAddProduct={() => setAddModalOpen(true)}
                    />

                    {isLoading && planograms.length === 0 && (
                        <S.LoadingMore>
                            <Spin size="small" /> Carregando produtos...
                        </S.LoadingMore>
                    )}

                    {isError && !shouldRedirect && planograms.length === 0 && (
                        <S.EmptyState>
                            <S.EmptyStateTitle>
                                Não foi possível carregar os produtos
                            </S.EmptyStateTitle>
                            <S.EmptyStateDescription>
                                Verifique sua conexão e tente novamente.
                            </S.EmptyStateDescription>
                            <Button onClick={() => refetch()}>
                                Tentar novamente
                            </Button>
                        </S.EmptyState>
                    )}

                    {!isLoading && !isError && planograms.length === 0 && (
                        <S.EmptyState>
                            <S.EmptyStateTitle>
                                Nenhum produto encontrado
                            </S.EmptyStateTitle>
                            <S.EmptyStateDescription>
                                Ajuste os filtros ou a busca para ver os
                                produtos.
                            </S.EmptyStateDescription>
                        </S.EmptyState>
                    )}

                    {planograms.length > 0 &&
                        (viewMode === 'cards' ? (
                            <S.CardsGrid className="qa-cards-grid">
                                {planograms.map((planogram) => (
                                    <ProductCard
                                        key={planogram.id}
                                        planogram={planogram}
                                        onEdit={setEditingPlanogram}
                                        onOpenProduct={handleOpenProduct}
                                    />
                                ))}
                            </S.CardsGrid>
                        ) : (
                            <ProductTable
                                planograms={planograms}
                                onEdit={setEditingPlanogram}
                                onEndReached={loadNextPage}
                                isFetchingNextPage={isFetchingNextPage}
                            />
                        ))}

                    {viewMode === 'cards' && (
                        <>
                            <div ref={sentinelRef} />

                            {isFetchingNextPage && (
                                <S.LoadingMore>
                                    <Spin size="small" /> Carregando mais
                                    produtos...
                                </S.LoadingMore>
                            )}
                        </>
                    )}
                </>
            )}

            <EditProductModal
                open={!!editingPlanogram}
                planogram={editingPlanogram}
                onClose={() => setEditingPlanogram(undefined)}
                onSaved={() => refetch()}
            />

            <AddProductModal
                open={addModalOpen}
                storeId={activeStoreId}
                onClose={() => setAddModalOpen(false)}
                onCreated={() => refetch()}
                onEditPlanogram={setEditingPlanogram}
            />
        </S.Container>
    );
};

export default PlanogramQuickAdjust;
